import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { X, ChevronLeft, ChevronRight, Camera, Image as ImageIcon } from 'lucide-react';
import { API } from '@/context/AuthContext';

/**
 * PremiumGallery — Editorial masonry gallery with category chips + lightbox.
 *
 * Sources (in order of preference):
 *   1. /api/content/gallery (admin-curated)
 *   2. Top partner venue photos (real BCI properties)
 *   3. A small curated lifestyle fallback (only if both above empty)
 *
 * Categories: auto-inferred from `category` field on gallery item, else "Lifestyle".
 */

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: Math.min(i, 8) * 0.04, ease: [0.22, 1, 0.36, 1] },
  }),
};

const FALLBACK = [
  { src: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=1200&q=80&auto=format&fit=crop', title: 'Affiliated Resort Stays', category: 'Hospitality' },
  { src: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200&q=80&auto=format&fit=crop', title: 'Partner Dining', category: 'Dining' },
  { src: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=1200&q=80&auto=format&fit=crop', title: 'Wellness Affiliations', category: 'Wellness' },
  { src: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=1200&q=80&auto=format&fit=crop', title: 'Boutique Hospitality Network', category: 'Hospitality' },
  { src: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=1200&q=80&auto=format&fit=crop', title: 'Curated Retreats', category: 'Hospitality' },
  { src: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80&auto=format&fit=crop', title: 'Beachside Partner Stays', category: 'Hospitality' },
  { src: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1200&q=80&auto=format&fit=crop', title: 'Members Networking', category: 'Networking' },
  { src: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=80&auto=format&fit=crop', title: 'Workation Spaces', category: 'Workation' },
  { src: 'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=1200&q=80&auto=format&fit=crop', title: 'Hospitality Network', category: 'Hospitality' },
];

const PremiumGallery = () => {
  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState('All');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Fetch admin gallery + partner photos in parallel
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let collected = [];
      try {
        const res = await axios.get(`${API}/content/gallery`);
        const list = (res.data || []).filter((g) => g.is_active);
        collected = list.map((g) => ({
          src: g.image_url,
          title: g.title || 'BITZ Moment',
          category: g.category || 'Lifestyle',
        }));
      } catch (_) {
        /* ignore */
      }
      // Backfill from partner photos if gallery is sparse
      if (collected.length < 8) {
        try {
          const res = await axios.get(`${API}/partners?is_active=true`);
          const venues = (res.data || []).slice(0, 24);
          venues.forEach((p) => {
            const url =
              p.image_url || (Array.isArray(p.photos) && p.photos[0]) || p.logo_url;
            if (url && !collected.find((c) => c.src === url)) {
              collected.push({
                src: url,
                title: p.name || 'Partner Venue',
                category: 'Affiliations',
              });
            }
          });
        } catch (_) {
          /* ignore */
        }
      }
      if (collected.length < 6) collected = collected.concat(FALLBACK);
      if (!cancelled) setItems(collected.slice(0, 18));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const categories = useMemo(() => {
    const set = new Set(items.map((i) => i.category || 'Lifestyle'));
    return ['All', ...Array.from(set)];
  }, [items]);

  const visible = useMemo(() => {
    if (activeCat === 'All') return items;
    return items.filter((i) => (i.category || 'Lifestyle') === activeCat);
  }, [items, activeCat]);

  // Lightbox keyboard nav
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const nextLightbox = useCallback(
    () => setLightboxIndex((i) => (i === null ? null : (i + 1) % visible.length)),
    [visible.length]
  );
  const prevLightbox = useCallback(
    () =>
      setLightboxIndex((i) =>
        i === null ? null : (i - 1 + visible.length) % visible.length
      ),
    [visible.length]
  );

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextLightbox();
      if (e.key === 'ArrowLeft') prevLightbox();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightboxIndex, closeLightbox, nextLightbox, prevLightbox]);

  // Masonry-style height variation pattern
  const tileSpan = (i) => {
    const pattern = ['row-span-2', 'row-span-1', 'row-span-1', 'row-span-2', 'row-span-1', 'row-span-1'];
    return pattern[i % pattern.length];
  };

  if (items.length === 0) return null;

  return (
    <section
      id="gallery"
      className="relative py-20 sm:py-28 lg:py-32 bg-[#0A0A0B] overflow-hidden"
      data-testid="premium-gallery"
    >
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.5), transparent 55%)',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-60px' }}
          variants={fadeUp}
          className="text-center mb-12 lg:mb-14 max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span
              className="text-[10px] sm:text-xs tracking-[0.4em] uppercase text-[#D4AF37] font-medium"
              style={{ fontFamily: 'Cinzel, Playfair Display, serif' }}
            >
              Inside the Network
            </span>
            <span className="h-px w-10 bg-[#D4AF37]" />
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-5"
            style={{ fontFamily: 'Playfair Display, Cinzel, serif' }}
          >
            Glimpses from our{' '}
            <span className="bg-gradient-to-r from-[#F5D77A] via-[#D4AF37] to-[#A67C00] bg-clip-text text-transparent">
              partner ecosystem.
            </span>
          </h2>
          <p
            className="text-base sm:text-lg text-gray-300/85 leading-relaxed"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            A look across the affiliated venues, hospitality partners and lifestyle
            collaborations that BITZ members get to access — a network we keep growing,
            curating, and opening up.
          </p>
        </motion.div>

        {/* Category chips */}
        {categories.length > 2 && (
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-10 lg:mb-12">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActiveCat(c)}
                data-testid={`gallery-chip-${c.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium tracking-wide transition-all duration-300 border ${
                  activeCat === c
                    ? 'bg-[#D4AF37] text-black border-[#D4AF37] shadow-[0_10px_30px_-12px_rgba(212,175,55,0.6)]'
                    : 'bg-white/[0.04] text-gray-300 border-white/10 hover:border-[#D4AF37]/40 hover:text-white'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {/* Masonry grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 auto-rows-[140px] sm:auto-rows-[170px] lg:auto-rows-[190px] gap-3 sm:gap-4">
          <AnimatePresence mode="popLayout">
            {visible.map((item, i) => (
              <motion.button
                layout
                key={`${item.src}-${i}`}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-30px' }}
                custom={i}
                variants={fadeUp}
                onClick={() => setLightboxIndex(i)}
                data-testid={`gallery-tile-${i}`}
                className={`group relative ${tileSpan(i)} rounded-2xl overflow-hidden bg-[#101012] border border-white/5 hover:border-[#D4AF37]/40 transition-all duration-500 cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/60`}
                aria-label={`View ${item.title}`}
              >
                <img
                  src={item.src}
                  alt={item.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1200ms] ease-out"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.parentElement.classList.add('bg-[#15151A]');
                  }}
                />
                {/* Caption overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 text-left translate-y-2 group-hover:translate-y-0 transition-transform duration-500 opacity-0 group-hover:opacity-100">
                  <p
                    className="text-[9px] tracking-[0.3em] uppercase text-[#D4AF37] mb-1"
                    style={{ fontFamily: 'Cinzel, serif' }}
                  >
                    {item.category || 'Lifestyle'}
                  </p>
                  <p
                    className="text-sm sm:text-base font-semibold text-white leading-tight line-clamp-2"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {item.title}
                  </p>
                </div>
                {/* Corner badge for affiliations */}
                {item.category === 'Affiliations' && (
                  <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm border border-white/10">
                    <p className="text-[8px] tracking-[0.2em] uppercase text-[#D4AF37] flex items-center gap-1">
                      <Camera className="w-2.5 h-2.5" /> Partner
                    </p>
                  </div>
                )}
              </motion.button>
            ))}
          </AnimatePresence>
        </div>

        {/* Empty state for filter */}
        {visible.length === 0 && (
          <div className="text-center py-16 text-gray-500 flex flex-col items-center gap-3">
            <ImageIcon className="w-10 h-10 text-[#D4AF37]/40" />
            <p className="text-sm">No moments captured in this category yet.</p>
          </div>
        )}

        {/* Network framing footnote */}
        <p className="text-center text-[11px] sm:text-xs text-gray-500 mt-10 sm:mt-12 max-w-2xl mx-auto leading-relaxed tracking-wide">
          Images represent the kind of hospitality, lifestyle and partner experiences
          BITZ members can access through our growing affiliation network. Venues belong
          to our partners; the access belongs to our members.
        </p>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && visible[lightboxIndex] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
            onClick={closeLightbox}
            data-testid="gallery-lightbox"
          >
            {/* Close */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 flex items-center justify-center text-white transition-colors"
              aria-label="Close"
              data-testid="lightbox-close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Prev */}
            {visible.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevLightbox();
                }}
                className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 flex items-center justify-center text-white transition-colors"
                aria-label="Previous"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}

            {/* Image */}
            <motion.div
              key={visible[lightboxIndex].src}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.35 }}
              className="relative max-w-5xl max-h-[88vh] flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={visible[lightboxIndex].src}
                alt={visible[lightboxIndex].title}
                className="max-w-full max-h-[78vh] object-contain rounded-xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]"
              />
              <div className="mt-4 text-center">
                <p
                  className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-1"
                  style={{ fontFamily: 'Cinzel, serif' }}
                >
                  {visible[lightboxIndex].category || 'Lifestyle'}
                </p>
                <h4
                  className="text-lg sm:text-xl text-white font-semibold"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {visible[lightboxIndex].title}
                </h4>
                <p className="text-xs text-gray-500 mt-2">
                  {lightboxIndex + 1} / {visible.length}
                </p>
              </div>
            </motion.div>

            {/* Next */}
            {visible.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextLightbox();
                }}
                className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 flex items-center justify-center text-white transition-colors"
                aria-label="Next"
                data-testid="lightbox-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default PremiumGallery;
